'use client';

/**
 * Discovery Composer Section
 *
 * @component_role Explorer section type `discovery_composer`
 * @business_rule The single coupled-state section for discovery mode. Holds
 *   the search composer state (text query + staged images) as local
 *   component state; writes turn records into discoveryStore.history when
 *   a search succeeds; watches discoveryStore.pendingRestoreQuery so the
 *   ConversationHistorySection can trigger a query restore.
 *
 * @user_outcome Describe what you need and/or drop reference images, then
 *   click Search. Multi-turn conversation state persists across turns via
 *   the store.
 * @integration_point klyde_edge /ui/data discovery_search strategy (via
 *   capability store resolution)
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain discovery
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Image as ImageIcon, Loader2, Plus, Search, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { useDiscovery } from '@/lib/api/hooks';
import { useTabStore } from '@/lib/stores/tab-store';
import { useDiscoveryStore } from '@/lib/stores/discovery-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { cn } from '@/lib/utils/cn';
import { processImageForObserve, isValidImageFile } from '@/lib/utils/image-processing';
import type {
  DiscoveryResponse,
  ExplorerSectionConfig,
  LabelsDict,
} from '@/lib/api/types';

// ============================================================
// Configuration: Design for 12, ship with 3
// ============================================================
const DISCOVERY_CONFIG = {
  SLOTS_PER_ROW: 3,
  MAX_IMAGES: 3, // Ship with 3, scale to 12 later (config change only)
  MAX_ROWS: 4, // 4 rows × 3 = 12 max
};

// ============================================================
// Helpers
// ============================================================
function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

// ============================================================
// Types
// ============================================================
interface StagedImage {
  id: string;
  base64: string; // Normalized 512×512 JPEG
  thumbnail: string; // Object URL for preview
  filename: string;
  sizeKB: number; // Post-normalization size
}

// ============================================================
// Image Slot (local — decoupled from StyleGuidance's copy on purpose)
//
// Sizing: each slot is `w-full aspect-square` and expects to live inside a
// grid cell that controls its width. This lets slots shrink proportionally
// with the explorer pane instead of clamping the pane to a fixed minimum.
// ============================================================
function ImageSlot({
  image,
  onAdd,
  onRemove,
  disabled,
}: {
  image?: StagedImage;
  onAdd: () => void;
  onRemove: () => void;
  disabled?: boolean;
}) {
  if (image) {
    return (
      <div className="relative group w-full aspect-square">
        <img
          src={image.thumbnail}
          alt={image.filename}
          className="w-full h-full object-cover rounded border border-border"
        />
        <button
          type="button"
          onClick={onRemove}
          className={cn(
            'absolute -top-1 -right-1 w-4 h-4 rounded-full',
            'bg-destructive text-destructive-foreground',
            'flex items-center justify-center',
            'opacity-0 group-hover:opacity-100 transition-opacity',
            'hover:bg-destructive/90',
          )}
        >
          <X className="w-2.5 h-2.5" />
        </button>
        <span className="absolute bottom-0 right-0 text-[8px] bg-background/80 px-0.5 rounded-tl">
          {image.sizeKB}KB
        </span>
      </div>
    );
  }

  if (disabled) {
    return <div className="w-full aspect-square" />;
  }

  return (
    <button
      type="button"
      onClick={onAdd}
      className={cn(
        'w-full aspect-square border-2 border-dashed border-muted-foreground/30 rounded',
        'flex items-center justify-center',
        'hover:border-primary hover:bg-accent/50 transition-colors',
      )}
    >
      <Plus className="w-4 h-4 text-muted-foreground" />
    </button>
  );
}

// ============================================================
// Main Section Component
// ============================================================
export function DiscoveryComposerSection({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config,
  labels: L,
}: {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}) {
  // Local input state — query and staged images are ephemeral user input,
  // not shared. The composer is the only writer and renderer of them.
  const [query, setQuery] = useState('');
  const [stagedImages, setStagedImages] = useState<StagedImage[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Shared state from discoveryStore — conversation id, history list, and
  // pending query restore events (set by ConversationHistorySection clicks).
  const conversationId = useDiscoveryStore((s) => s.conversationId);
  const setConversationId = useDiscoveryStore((s) => s.setConversationId);
  const history = useDiscoveryStore((s) => s.history);
  const addHistoryTurn = useDiscoveryStore((s) => s.addHistoryTurn);
  const clearConversation = useDiscoveryStore((s) => s.clearConversation);
  const pendingRestoreQuery = useDiscoveryStore((s) => s.pendingRestoreQuery);
  const clearPendingRestore = useDiscoveryStore((s) => s.clearPendingRestore);
  const pendingIntentSid = useDiscoveryStore((s) => s.pendingIntentSid);
  const pendingIntentName = useDiscoveryStore((s) => s.pendingIntentName);
  const clearPendingIntent = useDiscoveryStore((s) => s.clearPendingIntent);
  const setResponse = useDiscoveryStore((s) => s.setResponse);
  const setLoading = useDiscoveryStore((s) => s.setLoading);

  const openTab = useTabStore((state) => state.openTab);
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const discoveryStrategy = useCapabilityStore((s) => s.strategyName('discovery', 'search'));
  const discovery = useDiscovery(discoveryStrategy!);
  const { SLOTS_PER_ROW, MAX_IMAGES } = DISCOVERY_CONFIG;

  // ----------------------------------------------------------
  // Watch pendingRestoreQuery — ConversationHistorySection sets it when
  // the user clicks a history row. Apply it to our local query input,
  // then clear the flag on the store.
  // ----------------------------------------------------------
  useEffect(() => {
    if (pendingRestoreQuery !== null) {
      setQuery(pendingRestoreQuery);
      clearPendingRestore();
      inputRef.current?.focus();
    }
  }, [pendingRestoreQuery, clearPendingRestore]);

  // ----------------------------------------------------------
  // Watch pendingIntentSid — StyleGuideCard sets it when the user clicks
  // the search button. Fire a new search with intent_sid, clear conversation.
  // ----------------------------------------------------------
  useEffect(() => {
    if (pendingIntentSid) {
      const intentSid = pendingIntentSid;
      const intentName = pendingIntentName || intentSid;
      clearPendingIntent();
      clearConversation();
      setLoading(true);

      discovery.mutate(
        { intent_sid: intentSid },
        {
          onSuccess: (data: DiscoveryResponse) => {
            setConversationId(data.conversation_id);
            setResponse(data);

            if (data.out_of_domain && data.out_of_domain_type) {
              const msg = (L.ood_toast || "We don't carry {type}. Browse our collections instead.")
                .replace('{type}', data.out_of_domain_type);
              toast.warning(msg, { duration: 6000 });
            } else {
              const synthText = data.synthesis?.synthesis;
              if (synthText) {
                toast(synthText, { duration: 5000 });
              }
            }

            const title = `Style: ${intentName}`;
            addHistoryTurn({
              id: data.conversation_id,
              conversationId: data.conversation_id,
              query: title,
              imageCount: 0,
              timestamp: new Date(),
              resultCount: data.products?.total_count ?? data.products?.results?.length ?? 0,
              response: data,
            });
            openTab({
              id: `discovery-${data.conversation_id}`,
              type: 'discovery',
              title,
              entityId: intentSid,
            });
            // Re-select the DI so canvas stays on DI widget (Results tab shows products)
            selectNode({ id: intentSid, label: intentName, type: 'design_intent' });
          },
          onError: (error: Error) => {
            setLoading(false);
            toast.error(`${L.composer_search_failed}: ${error.message}`);
          },
        },
      );
    }
  }, [pendingIntentSid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ----------------------------------------------------------
  // Image Management
  // ----------------------------------------------------------
  const addImages = useCallback(
    async (files: File[]) => {
      const available = MAX_IMAGES - stagedImages.length;
      if (available <= 0) {
        toast.warning((L.composer_max_images || '{n} max').replace('{n}', String(MAX_IMAGES)));
        return;
      }

      const validFiles = files.filter(isValidImageFile);
      const filesToAdd = validFiles.slice(0, available);
      if (filesToAdd.length === 0) return;

      setIsProcessing(true);

      for (const file of filesToAdd) {
        try {
          // Normalize image: 512×512, JPEG 80%, EXIF stripped
          const { base64, sizeKB } = await processImageForObserve(file);

          setStagedImages((prev) => [
            ...prev,
            {
              id: generateId(),
              base64,
              thumbnail: URL.createObjectURL(file),
              filename: file.name,
              sizeKB,
            },
          ]);
        } catch (err) {
          console.error('[discovery-composer] Failed to process image:', file.name, err);
        }
      }

      setIsProcessing(false);
    },
    [stagedImages.length, MAX_IMAGES, L.composer_max_images],
  );

  const removeImage = useCallback((id: string) => {
    setStagedImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img) URL.revokeObjectURL(img.thumbnail);
      return prev.filter((i) => i.id !== id);
    });
  }, []);

  const clearImages = useCallback(() => {
    stagedImages.forEach((img) => URL.revokeObjectURL(img.thumbnail));
    setStagedImages([]);
  }, [stagedImages]);

  // ----------------------------------------------------------
  // Search Handler
  // ----------------------------------------------------------
  const handleSearch = useCallback(() => {
    const hasQuery = query.trim().length > 0;
    const hasImages = stagedImages.length > 0;

    if (!hasQuery && !hasImages) return;

    setLoading(true);

    discovery.mutate(
      {
        conversation_id: conversationId,
        query: hasQuery ? query.trim() : undefined,
        images: hasImages ? stagedImages.map((img) => img.base64) : undefined,
      },
      {
        onSuccess: (data: DiscoveryResponse) => {
          setConversationId(data.conversation_id);
          setResponse(data);

          // Toast: out-of-domain fallback or synthesis summary
          if (data.out_of_domain && data.out_of_domain_type) {
            const msg = (L.ood_toast || "We don't carry {type}. Browse our collections instead.")
              .replace('{type}', data.out_of_domain_type);
            toast.warning(msg, { duration: 6000 });
          } else {
            const synthText = data.synthesis?.synthesis;
            if (synthText) {
              toast(synthText, { duration: 5000 });
            }
          }

          // Build descriptive title
          const parts: string[] = [];
          if (hasQuery) parts.push(query.trim().slice(0, 20));
          if (hasImages) parts.push(`${stagedImages.length} ${L.history_image_suffix}`);
          const title = parts.join(' + ') || L.composer_search_button;

          // Add to shared history store
          addHistoryTurn({
            id: `${data.conversation_id}-${data.turn}`,
            conversationId: data.conversation_id,
            query: hasQuery ? query.trim() : '',
            imageCount: stagedImages.length,
            timestamp: new Date(),
            resultCount: data.products?.total_count || 0,
            response: data,
          });

          // Open Discovery tab with results
          openTab({
            id: `discovery-${data.conversation_id}`,
            type: 'discovery',
            title,
            entityId: data.conversation_id,
          });

          // Clear inputs
          setQuery('');
          clearImages();
        },
        onError: (error: Error) => {
          console.error('[discovery-composer] Search failed:', error.message, error);
          setLoading(false);
          toast.error(`${L.composer_search_failed}: ${error.message}`);
        },
      },
    );
  }, [
    query,
    stagedImages,
    conversationId,
    discovery,
    openTab,
    setResponse,
    setLoading,
    setConversationId,
    addHistoryTurn,
    clearImages,
    L.composer_search_failed,
    L.composer_search_button,
    L.history_image_suffix,
  ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch();
  };

  // ----------------------------------------------------------
  // Drag & Drop
  // ----------------------------------------------------------
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      const files = Array.from(e.dataTransfer.files);
      addImages(files);
    },
    [addImages],
  );

  // ----------------------------------------------------------
  // File Input
  // ----------------------------------------------------------
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    addImages(files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleNewConversation = () => {
    clearConversation(); // clears conversationId + history in store
    setQuery('');
    clearImages();
    inputRef.current?.focus();
  };

  const canSearch = query.trim().length > 0 || stagedImages.length > 0;
  const isPending = discovery.isPending || isProcessing;

  // Labels not loaded yet — render nothing rather than hardcoding English
  if (!L.composer_placeholder) return null;

  // ----------------------------------------------------------
  // Render
  // ----------------------------------------------------------
  return (
    <div className="flex flex-col">
      {/* Search Input Area */}
      <div
        className={cn('p-3 border-b border-border', dragActive && 'bg-primary/10 border-primary')}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <form onSubmit={handleSubmit}>
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={dragActive ? L.composer_drop_active : L.composer_placeholder}
              className={cn(
                'w-full pl-9 pr-12 py-2 text-sm rounded-md',
                'bg-muted border border-border',
                'focus:outline-none focus:ring-1 focus:ring-primary',
                'placeholder:text-muted-foreground',
              )}
              disabled={isPending}
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />

            {/* Loading indicator in input */}
            {isPending && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-primary" />
            )}
          </div>
        </form>

        {/* Conversation context indicator */}
        {conversationId && (
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {L.composer_turn_of_conversation.replace('{n}', String(history.length))}
            </span>
            <button
              onClick={handleNewConversation}
              className="flex items-center gap-1 hover:text-foreground"
            >
              <X className="w-3 h-3" />
              {L.composer_new_search}
            </button>
          </div>
        )}
      </div>

      {/* Image Staging Area — responsive grid so slots shrink with the pane */}
      <div
        className={cn('px-3 py-3 border-b border-border', dragActive && 'bg-primary/5')}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${SLOTS_PER_ROW}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: MAX_IMAGES }).map((_, slotIndex) => (
            <ImageSlot
              key={slotIndex}
              image={stagedImages[slotIndex]}
              onAdd={() => fileInputRef.current?.click()}
              onRemove={() => removeImage(stagedImages[slotIndex]?.id)}
              disabled={slotIndex > stagedImages.length}
            />
          ))}
        </div>

        {/* Count indicator */}
        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
          <ImageIcon className="w-3 h-3 shrink-0" />
          <span className="truncate">
            {L.composer_images_counter
              .replace('{current}', String(stagedImages.length))
              .replace('{max}', String(MAX_IMAGES))}
          </span>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Search Button */}
      <div className="px-3 py-2 border-b border-border">
        <button
          type="button"
          onClick={handleSearch}
          disabled={!canSearch || isPending}
          className={cn(
            'w-full py-2 px-3 rounded-md text-sm font-medium',
            'flex items-center justify-center gap-2',
            'bg-primary text-primary-foreground',
            'hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed',
            'transition-colors',
          )}
        >
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {isProcessing ? L.composer_processing : L.composer_searching}
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              {L.composer_search_button}
            </>
          )}
        </button>
      </div>

      {/* Error display (search mutation error) */}
      {discovery.isError && (
        <div className="p-3 border-b border-border text-sm text-destructive">
          {L.composer_search_failed}: {discovery.error?.message || L.composer_unknown_error}
        </div>
      )}
    </div>
  );
}
